/**
 * HotspotRewardTable — W2.2 / Contract MR-v1, erweitert in R3/FE-6
 *
 * Flache Tabelle mit einer Zeile pro Hotspot — Struktur der Design-Vorlage
 * (monthly-report.html .h-row): Hotspot | HNT | GB | Commission | Net EUR,
 * IMMER alle 5 Spalten (kein Ein-/Ausblenden je nach Testdaten). Scrollt
 * horizontal auf < 700px (overflow-x: auto).
 *
 * P1 (Design-Audit 2026-09-01): Zeitraum-Spalte und verschachtelte
 * Share-Unterzeilen wurden entfernt — die Vorlage kennt nur eine flache Zeile
 * je Hotspot. Die Split-Aufschluesselung (R3/BE-7) fliesst stattdessen als
 * eine "Commission"-Spalte ein: Summe der Split-Prozentsaetze, die dem
 * Hotspot-Eigentuemer nicht zufliessen (z.B. "−25%"), analog zur Vorlage.
 *
 * P1 (Design-Audit 2026-09-01, Folgefund): die HNT-Spalte war zuvor nur bei
 * mindestens einem Wert > 0 sichtbar (ebenso IOT/MOBILE) — die Vorlage hat
 * feste Spalten unabhaengig vom Datenstand. HNT ist jetzt immer sichtbar.
 * IOT/MOBILE-Werte pro Hotspot kennt die Vorlage nicht (nur als Summe im
 * KPI-Strip/Token-Total-Block der ReportHero) und entfallen hier entsprechend.
 * Eine GB-Spalte (Datenvolumen) liefert der monthly_reports-Endpoint nicht —
 * das ist eine Design-Annahme ohne Entsprechung in api/v2 (kein neuer
 * Backend-Endpunkt, siehe Aufgabenregeln) und zeigt daher strukturell "—".
 */
import { useTranslation } from 'react-i18next'
import type { HotspotReward } from './useMonthlyReport'
import { formatCurrency } from './reportUtils'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'

const TOKEN_COLORS = {
  hnt: 'var(--token-hnt,    #474DFF)',
}

type Props = {
  hotspots: HotspotReward[]
  /** BE #19: Kontowaehrung fuer die Netto-Spalte, aus summary.currency. */
  currency: string
}

/** Physische Adresse "Street, City" — leere Teile werden ausgelassen. */
function physicalAddress(hotspot: HotspotReward): string {
  return [hotspot.street, hotspot.city].filter((part) => !!part && part.trim()).join(', ')
}

/**
 * Commission-Spalte: Summe der Prozentsaetze aus den Provisions-Regeln
 * (host_split_rules, Issue #53) — "—" wenn keine Regel existiert.
 */
function commissionPercentage(hotspot: HotspotReward): number {
  return (hotspot.shares ?? []).reduce((sum, s) => sum + s.percentage, 0)
}

/**
 * Fixbetrag-Regeln haben keinen Prozentsatz. Sie werden je Auszahlungswaehrung
 * summiert und neben/statt dem Prozentwert ausgewiesen, damit sie nicht als
 * "0 %" bzw. "—" verschwinden.
 */
function fixedCommissions(hotspot: HotspotReward): string[] {
  const byCurrency = new Map<string, number>()
  for (const s of hotspot.shares ?? []) {
    if (s.split_type !== 'fixed_amount' || !s.fixed_amount) continue
    const currency = s.payout_currency || 'HNT'
    byCurrency.set(currency, (byCurrency.get(currency) ?? 0) + s.fixed_amount)
  }
  return [...byCurrency].map(([currency, amount]) => `−${amount.toFixed(2)} ${currency}`)
}

export function HotspotRewardTable({ hotspots, currency }: Props) {
  const { t } = useTranslation('reports')

  return (
    <div className="card" style={{ overflowX: 'auto', marginBottom: 20 }}>
      <table className="table" aria-label={t('table.ariaLabel')}>
        <thead>
          <tr>
            <th scope="col">{t('table.colHotspot')}</th>
            <th scope="col" style={{ textAlign: 'right', color: TOKEN_COLORS.hnt }}>
              HNT
            </th>
            <th scope="col" style={{ textAlign: 'right' }}>
              GB
            </th>
            <th scope="col" style={{ textAlign: 'right' }}>
              {t('table.colCommission')}
            </th>
            <th scope="col" style={{ textAlign: 'right' }}>
              {t('table.colNet', { currency })}
            </th>
          </tr>
        </thead>
        <tbody>
          {hotspots.map((h, idx) => {
            const address = physicalAddress(h)
            const rowKey = `${h.id || 'hs'}-${idx}`
            const commissionPct = commissionPercentage(h)
            const fixedParts = fixedCommissions(h)
            const commissionParts = [
              ...(commissionPct > 0
                ? [t('table.commissionValue', { percentage: commissionPct })]
                : []),
              ...fixedParts,
            ]
            return (
              <tr key={rowKey}>
                <td style={{ fontWeight: 500 }}>
                  <div>{hotspotDisplayName(h.name, h.address, t('table.unknownHotspot'))}</div>
                  {address && (
                    <div
                      className="muted hotspot-address"
                      style={{ fontWeight: 400, fontSize: 12 }}
                    >
                      {address}
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {h.rewards_hnt.toFixed(2)}
                </td>
                {/* GB (Datenvolumen) liefert api/v2 nicht — kein neuer Backend-
                    Endpunkt, siehe Datei-Kommentar oben. */}
                <td
                  className="muted"
                  style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  —
                </td>
                <td
                  className="muted"
                  style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {commissionParts.length > 0 ? commissionParts.join(' · ') : '—'}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 500,
                  }}
                >
                  {formatCurrency(h.rewards_fiat, currency)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
